/**
 * @module agent/adapters/services/SearchWorkflowService
 *
 * Pure state machine for search workflow management.
 * Zero dependencies - all functions are pure (state in, new state out).
 *
 * The search workflow follows these phases:
 * 1. SEARCH_STARTED - Agent initiates search via [SEARCH_STARTED:tool:id] tag
 * 2. POLLING - Auto-poll every 20 seconds via hidden messages
 * 3. BUSY - Agent responds with [BUSY] while search in progress
 * 4. COMPLETE - Agent returns final results (no tags)
 * 5. TIMEOUT - Search times out after max attempts (5 minutes)
 *
 * @example
 * import { createInitialState, startSearch, isActive } from './SearchWorkflowService';
 *
 * let state = createInitialState();
 * state = startSearch(state, { toolName: 'search', searchId: 'id', sessionId: 'session' });
 * console.log(isActive(state)); // true
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Progress phases emitted during search lifecycle.
 * Maps to user-facing loading sections in ProfilerSearchResultsLoaderNew.
 */
export const SEARCH_PROGRESS_PHASES = {
  INITIATING: 'INITIATING',
  ANALYZING: 'ANALYZING',
  EVALUATING: 'EVALUATING',
  RATIONALE: 'RATIONALE',
  COMPLETE: 'COMPLETE'
};

/**
 * Reason codes returned by handleSearchTags / extractFinalResponse.
 * Used for logging, testing, and response routing.
 */
export const SEARCH_TAG_REASONS = {
  SEARCH_INITIATION: 'search_initiation',
  BUSY_RESPONSE: 'busy_response',
  BUSY_WRONG_SESSION: 'busy_wrong_session',
  SEARCH_COMPLETED: 'search_completed',
  NORMAL_RESPONSE: 'normal_response',
  INVALID_CONTENT: 'invalid_content',
  VALIDATION_ERROR: 'validation_error',
  DESTROYED: 'destroyed'
};

/**
 * Marker returned by removeSearchTags when content is only a search tag.
 */
export const SEARCH_INITIATION_MARKER = '[SEARCH_INITIATION]';

/**
 * Search workflow configuration constants.
 */
export const SEARCH_CONFIG = {
  /** Polling interval in milliseconds (20 seconds) */
  POLL_INTERVAL_MS: 20000,

  /** Maximum poll attempts before timeout (15 × 20s = 5 minutes) */
  MAX_POLL_ATTEMPTS: 15,

  /** Prefix for poll check messages */
  POLL_MESSAGE_PREFIX: 'CHECK_SEARCH_RESULTS:'
};

// ============================================================================
// TYPE DEFINITIONS (JSDoc)
// ============================================================================

/**
 * @typedef {Object} SearchState
 * @property {boolean} active - Whether a search is currently active
 * @property {string|null} toolName - Name of the search tool
 * @property {string|null} searchId - Unique search identifier
 * @property {string|null} sessionId - Session where search was initiated
 * @property {number|null} startTime - Timestamp when search started
 * @property {number} pollAttempts - Number of poll attempts made
 */

/**
 * @typedef {Object} SearchTagsResult
 * @property {boolean} hasSearchStart - Whether SEARCH_STARTED tag is present
 * @property {boolean} hasBusy - Whether BUSY tag is present
 * @property {string|null} toolName - Extracted tool name
 * @property {string|null} searchId - Extracted search ID
 */

// ============================================================================
// STATE FACTORY
// ============================================================================

/**
 * Create initial search state.
 * Returns a fresh state object each time (immutable pattern).
 *
 * @returns {SearchState} Initial inactive search state
 */
export function createInitialState() {
  return {
    active: false,
    toolName: null,
    searchId: null,
    sessionId: null,
    startTime: null,
    pollAttempts: 0
  };
}

// ============================================================================
// STATE TRANSITIONS (Pure Functions)
// ============================================================================

/**
 * Start a new search workflow.
 * Pure function: returns new state, does not mutate input.
 *
 * @param {SearchState} state - Current state
 * @param {Object} params - Search parameters
 * @param {string} params.toolName - Name of the search tool
 * @param {string} params.searchId - Unique search identifier
 * @param {string} params.sessionId - Session where search was initiated
 * @returns {SearchState} New state with search active
 */
export function startSearch(state, { toolName, searchId, sessionId }) {
  return {
    ...state,
    active: true,
    toolName,
    searchId,
    sessionId,
    startTime: Date.now(),
    pollAttempts: 0
  };
}

/**
 * Handle BUSY response from agent.
 * Pure function: returns new state, does not mutate input.
 * The state stays active - BUSY means keep waiting.
 *
 * @param {SearchState} state - Current state
 * @returns {SearchState} New state (unchanged but new object)
 */
export function handleBusyResponse(state) {
  return { ...state };
}

/**
 * Increment poll attempt counter.
 * Pure function: returns new state, does not mutate input.
 *
 * @param {SearchState} state - Current state
 * @returns {SearchState} New state with incremented pollAttempts
 */
export function incrementPollAttempt(state) {
  return {
    ...state,
    pollAttempts: state.pollAttempts + 1
  };
}

/**
 * Complete the search workflow.
 * Pure function: returns new state, does not mutate input.
 *
 * @param {SearchState} state - Current state
 * @returns {SearchState} New inactive state (reset)
 */
export function completeSearch(state) {
  return createInitialState();
}

/**
 * Handle search timeout.
 * Pure function: returns new state, does not mutate input.
 * Same as completeSearch - resets to initial state.
 *
 * @param {SearchState} state - Current state
 * @returns {SearchState} New inactive state (reset)
 */
export function handleTimeout(state) {
  return createInitialState();
}

// ============================================================================
// STATE QUERIES (Pure Functions)
// ============================================================================

/**
 * Check if a search is currently active.
 *
 * @param {SearchState|null|undefined} state - Current state
 * @returns {boolean} True if search is active
 */
export function isActive(state) {
  return state?.active === true;
}

/**
 * Check if a search is currently active for a specific session.
 *
 * @param {SearchState|null|undefined} state - Current state
 * @param {string} sessionId - Session ID to check
 * @returns {boolean} True if search is active AND belongs to this session
 */
export function isActiveForSession(state, sessionId) {
  return isActive(state) && state.sessionId === sessionId;
}

/**
 * Check if search has timed out (exceeded max poll attempts).
 *
 * @param {SearchState|null|undefined} state - Current state
 * @returns {boolean} True if timed out
 */
export function isTimedOut(state) {
  if (!state) return false;
  return state.pollAttempts > SEARCH_CONFIG.MAX_POLL_ATTEMPTS;
}

/**
 * Check if another poll should be scheduled.
 * Returns false if inactive or at max attempts.
 *
 * @param {SearchState|null|undefined} state - Current state
 * @returns {boolean} True if should schedule poll
 */
export function shouldSchedulePoll(state) {
  if (!isActive(state)) return false;
  return state.pollAttempts < SEARCH_CONFIG.MAX_POLL_ATTEMPTS;
}

/**
 * Get search duration in seconds.
 *
 * @param {SearchState|null|undefined} state - Current state
 * @returns {number} Duration in seconds, or 0 if no active search
 */
export function getSearchDuration(state) {
  if (!state?.startTime) return 0;
  return (Date.now() - state.startTime) / 1000;
}

// ============================================================================
// CONTENT PARSING (Pure Functions)
// ============================================================================

// RFC-4122 UUID. The agent emits [SEARCH_STARTED] with the real searchId; a model
// occasionally writes a placeholder id (e.g. "pending") before the real one is available.
const SEARCH_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True if `id` is a valid UUID. Used to reject placeholder search ids (e.g. "pending")
 * so we never start polling a non-existent search. TD-2274.
 *
 * @param {string|null|undefined} id
 * @returns {boolean}
 */
export function isValidSearchId(id) {
  return typeof id === 'string' && SEARCH_ID_RE.test(id);
}

/**
 * Parse search-related tags from content.
 *
 * @param {string|null|undefined} content - Content to parse
 * @returns {SearchTagsResult} Parsed tag information
 */
export function parseSearchTags(content) {
  const result = {
    hasSearchStart: false,
    hasBusy: false,
    toolName: null,
    searchId: null
  };

  if (!content || typeof content !== 'string') {
    return result;
  }

  // A single response may contain more than one [SEARCH_STARTED:tool:id] tag — the model
  // sometimes emits a placeholder id (e.g. "pending") before the real one. Scan all tags
  // and accept only one whose id is a valid UUID, so we never poll a bogus id. TD-2274.
  for (const match of content.matchAll(/\[SEARCH_STARTED:(\w+):([^\]]+)\]/g)) {
    if (isValidSearchId(match[2])) {
      result.hasSearchStart = true;
      result.toolName = match[1];
      result.searchId = match[2];
      break;
    }
  }

  // Check for BUSY tag
  if (content.includes('[BUSY]')) {
    result.hasBusy = true;
  }

  return result;
}

/**
 * Remove search tags from content for display.
 *
 * @param {string|null|undefined} content - Content to clean
 * @returns {string|null|undefined} Cleaned content
 */
export function removeSearchTags(content) {
  if (!content || typeof content !== 'string') {
    return content;
  }

  // Remove SEARCH_STARTED tags
  let cleaned = content.replaceAll(/\[SEARCH_STARTED:[^\]]+\]/g, '');

  // Remove BUSY tags
  cleaned = cleaned.replaceAll('[BUSY]', '');

  // Clean up extra whitespace
  cleaned = cleaned.trim();

  // If content becomes empty after removing tags, return special marker
  if (!cleaned && content.includes('[SEARCH_STARTED:')) {
    return SEARCH_INITIATION_MARKER;
  }

  return cleaned;
}

/**
 * Extract search ID from content.
 *
 * @param {string|null|undefined} content - Content to parse
 * @returns {string|null} Extracted search ID or null
 */
export function extractSearchId(content) {
  if (!content || typeof content !== 'string') {
    return null;
  }

  const match = /\[SEARCH_STARTED:\w+:([^\]]+)\]/.exec(content);
  return match ? match[1] : null;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create poll message content.
 *
 * @param {string} searchId - Search ID to check
 * @returns {string} Poll message content
 */
export function createPollMessage(searchId) {
  return `${SEARCH_CONFIG.POLL_MESSAGE_PREFIX}${searchId}`;
}

/**
 * Check if content is a poll message.
 *
 * @param {string|null|undefined} content - Content to check
 * @returns {boolean} True if poll message
 */
export function isPollMessage(content) {
  return content?.startsWith(SEARCH_CONFIG.POLL_MESSAGE_PREFIX) || false;
}
